'use client';
import Link from 'next/link';
import { Button, Icon, Typography } from '@workspace/ui/common';
import type { INavigationGroup, INavigationItem } from '../interfaces';
import { cn, getLinkUrl, isLinkActive, handleNavigationClick } from '@/app/lib';

interface MobileNavigationProps {
  isOpen: boolean;
  currentLevel: number;
  navigationStack: {
    title: string;
    items: (INavigationItem | INavigationGroup)[];
  }[];
  activePath?: string;
  onActivePathChange?: (newPath: string) => void;
  onBack: () => void;
  onItemClick: (item: INavigationItem | INavigationGroup) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  primaryButton?: { label?: string; link?: any } | null;
}

const isNavigationGroup = (item: INavigationItem | INavigationGroup): item is INavigationGroup => {
  return (item as INavigationGroup)._type === 'navigationGroup';
};

export const MobileNavigation = (props: MobileNavigationProps) => {
  const {
    isOpen,
    currentLevel,
    navigationStack,
    activePath,
    onActivePathChange,
    onBack,
    onItemClick,
    containerRef,
    primaryButton,
  } = props;

  const currentItems = navigationStack[currentLevel]?.items || [];

  return (
    <div
      ref={containerRef}
      className={`xl:hidden fixed top-18 right-0 w-4/5 h-[calc(100vh-4rem)] bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          {currentLevel > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
              <button
                onClick={onBack}
                className="flex items-center text-gray-900 hover:text-primary-500 transition-colors duration-200"
              >
                <Icon name="ChevronLeft" size={20} className="mr-3" />
                <Typography variant="bodyLg">{navigationStack[currentLevel]?.title}</Typography>
              </button>
            </div>
          )}

          <ul>
            {currentItems.map((item, index) => {
              if (isNavigationGroup(item)) {
                const hasSubItems = item.items && item.items.length > 0;
                return (
                  <li key={index} className="border-b border-secondary-100">
                    <div onClick={() => onItemClick(item)}>
                      {item.link ? (
                        <Link
                          href={getLinkUrl(item.link)}
                          {...(item.link.type === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className="pl-14 w-full flex items-center justify-between py-4 px-6 text-left text-gray-900 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                        >
                          <Typography variant="bodyLg" className="font-medium text-gray-900">
                            {item.groupTitle}
                          </Typography>
                          {hasSubItems && <Icon name="ChevronRight" size={18} className="text-secondary-400" />}
                        </Link>
                      ) : (
                        <Button className="pl-14 w-full flex items-center justify-between py-4 px-6 text-left text-gray-900 hover:text-primary-500 transition-colors duration-200">
                          <Typography variant="bodyLg" className="font-medium text-gray-900 text-base">
                            {item.groupTitle}
                          </Typography>
                          {hasSubItems && <Icon name="ChevronRight" size={18} className="text-secondary-400" />}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              } else {
                const isActive = isLinkActive(item.link, activePath);
                return (
                  <li key={index} className="border-b border-secondary-100">
                    <div onClick={() => onItemClick(item)}>
                      <Link
                        href={getLinkUrl(item.link)}
                        {...(item.link?.type === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className={cn(
                          'pl-14 block w-full text-left py-4 px-6 transition-colors duration-200 font-medium leading-7 cursor-pointer text-base',
                          isActive ? 'text-primary-500' : 'text-gray-900 hover:text-primary-500',
                        )}
                      >
                        {item.label}
                      </Link>
                    </div>
                  </li>
                );
              }
            })}

            {primaryButton && primaryButton.link && currentLevel === 0 && (
              <li className="border-b border-secondary-100">
                <div
                  onClick={() => {
                    handleNavigationClick(primaryButton.link, onActivePathChange);
                  }}
                >
                  <Link
                    href={getLinkUrl(primaryButton.link)}
                    {...(primaryButton.link.type === 'external'
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    className={cn(
                      'pl-14 block w-full text-left py-4 px-6 transition-colors duration-200 font-medium leading-7 cursor-pointer text-base',
                      isLinkActive(primaryButton.link, activePath)
                        ? 'text-primary-500'
                        : 'text-gray-900 hover:text-primary-500',
                    )}
                  >
                    {primaryButton.label}
                  </Link>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};
